import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';

type TurnstileApi = {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Cloudflare Turnstile site key (public); without it there is no widget and Supabase's captcha is off. Never
 * against a local Supabase: its captcha is off (config.toml) and a production key does not render on localhost.
 */
export function turnstileSiteKey(env: Record<string, unknown> = import.meta.env): string | undefined {
  const key = typeof env.VITE_TURNSTILE_SITE_KEY === 'string' ? env.VITE_TURNSTILE_SITE_KEY : '';
  let local = false;
  try {
    local = ['localhost', '127.0.0.1'].includes(new URL(String(env.VITE_SUPABASE_URL ?? '')).hostname);
  } catch {
    // no Supabase URL: local-only mode, no auth forms anyway
  }
  return key && !local ? key : undefined;
}

let loading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loading = null;
      reject(new Error('turnstile'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * The Turnstile widget (P6 Ruling 5): Supabase Auth verifies its token (`captchaToken`), so the app has no
 * verifier of its own. Loaded by a plain script tag (no npm package).
 */
export function Turnstile({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string | undefined;
  onToken: (token: string | null) => void;
  onError?: () => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  const cb = useRef(onToken);
  cb.current = onToken;
  const fail = useRef(onError);
  fail.current = onError;
  useEffect(() => {
    if (!siteKey || !el.current) return;
    let alive = true;
    let id: string | null = null;
    loadScript().then(
      () => {
        if (!alive || !el.current || !window.turnstile) return;
        id = window.turnstile.render(el.current, {
          sitekey: siteKey,
          theme: 'auto',
          callback: (t: string) => cb.current(t),
          'expired-callback': () => cb.current(null),
          'error-callback': () => {
            cb.current(null);
            fail.current?.();
          },
        });
      },
      () => {
        cb.current(null);
        fail.current?.();
      },
    );
    return () => {
      alive = false;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey]);
  return siteKey ? <div ref={el} /> : null;
}

/**
 * Form state for a captcha: `ready` is true without a site key, else once a token arrived. A widget that fails
 * (network, content blocker) says so and offers a retry instead of leaving the submit button silently disabled.
 */
export function useCaptcha(siteKey: string | undefined = turnstileSiteKey()) {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const widget = siteKey ? (
    <div className="flex flex-col gap-2">
      <Turnstile key={attempt} siteKey={siteKey} onToken={setToken} onError={() => setFailed(true)} />
      {failed && (
        <div className="flex flex-wrap items-center gap-2">
          <p role="alert" className="font-sans text-xs text-danger">
            {t('auth.captchaFailed')}
          </p>
          <Button
            onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}
          >
            {t('auth.retry')}
          </Button>
        </div>
      )}
    </div>
  ) : null;
  return {
    token: token ?? undefined,
    ready: !siteKey || token !== null,
    widget,
    // A token is single-use: asking again means a new challenge, or the form waits for the old one to expire.
    reset: () => {
      setToken(null);
      setAttempt((n) => n + 1);
    },
  };
}
